import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Alert,
  Card,
  CardMedia,
  CardActions,
  Chip,
  Divider,
} from '@mui/material';
import FormSelect from '../../components/common/FormSelect';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const QuestionForm = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Para edición
  const isEditing = Boolean(id);
  
  // Obtener parámetros de la URL
  const searchParams = new URLSearchParams(window.location.search);
  const fromSets = searchParams.get('from') === 'sets';
  const prefilledSetId = searchParams.get('set_id') || '';

  const [formData, setFormData] = useState({
    question: '',
    options: ['', '', '', ''],
    correct_answer: '',
    area: '',
    topic: '',
    competency: '',
    component: '',
    context_text: '',
    question_set_id: prefilledSetId,
    question_order: '',
    explanation: '',
    difficulty: '',
    status: 'active',
  });

  const [contextImage, setContextImage] = useState(null);
  const [questionImage, setQuestionImage] = useState(null);
  const [contextImagePreview, setContextImagePreview] = useState('');
  const [questionImagePreview, setQuestionImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingQuestion, setLoadingQuestion] = useState(isEditing);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [structure, setStructure] = useState(null);
  const [availableSets, setAvailableSets] = useState([]);

  useEffect(() => {
    fetchStructure();
    fetchAvailableSets();
    if (isEditing) {
      loadQuestion();
    }
  }, [id]);

  const fetchStructure = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/structure`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStructure(response.data);
    } catch (err) {
      console.error('Error fetching structure:', err);
    }
  };

  const fetchAvailableSets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/question-sets/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableSets(response.data.sets || []);
    } catch (err) {
      console.error('Error fetching available sets:', err);
    }
  };

  const loadQuestion = async () => {
    setLoadingQuestion(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/questions/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const question = response.data;
      
      console.log('Pregunta cargada desde DB:', {
        id: question.id,
        correct_answer: question.correct_answer,
        competency: question.competency,
        topic: question.topic,
        component: question.component,
        area: question.area
      });
      
      // Mapear status antiguo a nuevo formato
      let mappedStatus = question.status || 'active';
      if (mappedStatus === 'pending' || mappedStatus === 'validated') {
        mappedStatus = 'active';
      } else if (mappedStatus === 'rejected') {
        mappedStatus = 'inactive';
      }
      
      // Asegurar que options es un array
      let options = question.options;
      if (typeof options === 'string') {
        try {
          options = JSON.parse(options);
        } catch (e) {
          options = ['', '', '', ''];
        }
      }
      if (!Array.isArray(options)) {
        options = ['', '', '', ''];
      }
      
      // Validar correct_answer
      const validAnswers = ['A', 'B', 'C', 'D'];
      const validCorrectAnswer = validAnswers.includes(question.correct_answer) 
        ? question.correct_answer 
        : '';
      
      console.log('Validando correct_answer:', {
        original: question.correct_answer,
        validado: validCorrectAnswer
      });
      
      // Validar que los valores de clasificación existen en structure
      // Solo si structure está cargada
      let validTopic = question.topic || '';
      let validCompetency = question.competency || '';
      let validComponent = question.component || '';
      
      if (structure && structure[question.area]) {
        const validateValue = (value, list) => {
          if (!value || !list) return '';
          const exists = list.some(item => item.id === value);
          console.log(`Validando ${value}:`, { exists, listLength: list?.length });
          return exists ? value : '';
        };
        
        const areaStructure = structure[question.area];
        validTopic = validateValue(question.topic, areaStructure.topics);
        validCompetency = validateValue(question.competency, areaStructure.competencies);
        validComponent = validateValue(question.component, areaStructure.components);
        
        console.log('Valores validados:', {
          topic: validTopic,
          competency: validCompetency,
          component: validComponent
        });
      }
      
      setFormData({
        question: question.question,
        options: options,
        correct_answer: validCorrectAnswer,
        area: question.area,
        topic: validTopic,
        competency: validCompetency,
        component: validComponent,
        context_text: question.context_text || '',
        question_set_id: question.question_set_id || '',
        question_order: question.question_order || '',
        explanation: question.explanation || '',
        difficulty: question.difficulty,
        status: mappedStatus,
      });

      if (question.context_image) {
        setContextImagePreview(question.context_image);
      }
      if (question.question_image) {
        setQuestionImagePreview(question.question_image);
      }
    } catch (err) {
      setError('Error al cargar la pregunta');
      console.error(err);
    } finally {
      setLoadingQuestion(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Si cambia el área, limpiar campos dependientes
    if (name === 'area') {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        topic: '', // Limpiar tema
        competency: '', // Limpiar competencia
        component: '', // Limpiar componente
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleOptionChange = (index, value) => {
    setFormData((prev) => {
      const newOptions = [...prev.options];
      newOptions[index] = value;
      return {
        ...prev,
        options: newOptions,
      };
    });
  };

  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tamaño (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('La imagen no debe superar 5MB');
        return;
      }

      // Validar tipo
      if (!file.type.startsWith('image/')) {
        setError('Solo se permiten archivos de imagen');
        return;
      }

      if (type === 'context') {
        setContextImage(file);
        setContextImagePreview(URL.createObjectURL(file));
      } else {
        setQuestionImage(file);
        setQuestionImagePreview(URL.createObjectURL(file));
      }
    }
  };

  const removeImage = (type) => {
    if (type === 'context') {
      setContextImage(null);
      setContextImagePreview('');
    } else {
      setQuestionImage(null);
      setQuestionImagePreview('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validar que todas las opciones estén llenas
      if (formData.options.some((opt) => !opt.trim())) {
        setError('Todas las opciones deben tener contenido');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();

      // Agregar campos de texto
      Object.keys(formData).forEach((key) => {
        if (key === 'options') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else if (formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Log para debug
      console.log('📤 Sending formData with status:', formData.status);
      console.log('📋 Full formData:', formData);

      // Agregar imágenes si existen
      if (contextImage) {
        formDataToSend.append('context_image', contextImage);
      }
      if (questionImage) {
        formDataToSend.append('question_image', questionImage);
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      };

      if (isEditing) {
        await axios.put(`${API_URL}/api/questions/${id}`, formDataToSend, config);
        setSuccess('Pregunta actualizada exitosamente');
      } else {
        await axios.post(`${API_URL}/api/questions`, formDataToSend, config);
        setSuccess('Pregunta creada exitosamente');
        // Limpiar formulario
        setFormData({
          question: '',
          options: ['', '', '', ''],
          correct_answer: 'A',
          area: 'matematicas',
          topic: '',
          competency: '',
          component: '',
          context_text: '',
          question_set_id: '',
          question_order: '',
          explanation: '',
          difficulty: 'medium',
          status: 'pending',
        });
        setContextImage(null);
        setQuestionImage(null);
        setContextImagePreview('');
        setQuestionImagePreview('');
      }

      setTimeout(() => {
        navigate('/teacher/questions');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar la pregunta');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Funciones helper para obtener datos filtrados
  const getTopicsForArea = () => {
    if (!structure || !formData.area) return [];
    const areaData = structure.areas[formData.area];
    
    if (formData.area === 'ciencias' && areaData?.components) {
      const allTopics = [];
      areaData.components.forEach(comp => {
        if (comp.topics) {
          comp.topics.forEach(topic => {
            allTopics.push({
              ...topic,
              componentId: comp.id,
              componentName: comp.name,
            });
          });
        }
      });
      return allTopics;
    }
    
    return areaData?.topics || [];
  };

  const getCompetencies = () => {
    if (!structure || !formData.area) return [];
    return structure.areas[formData.area]?.competencies || [];
  };

  const getComponents = () => {
    if (!structure || formData.area !== 'ciencias') return [];
    return structure.areas.ciencias?.components || [];
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(fromSets ? '/teacher/question-sets' : '/teacher/questions')}
          sx={{ mb: 2 }}
        >
          Volver a {fromSets ? 'Question Sets' : 'Preguntas'}
        </Button>
        <Typography variant="h4" gutterBottom>
          {isEditing ? 'Editar Pregunta' : 'Crear Nueva Pregunta'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loadingQuestion ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography>Cargando pregunta...</Typography>
        </Paper>
      ) : (
      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          {/* Sección: Clasificación */}
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Clasificación
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          {/* Sección de Clasificación */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
              Clasificación
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* Área y Dificultad */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <FormSelect
                    label="Área"
                    name="area"
                    value={formData.area}
                    onChange={handleInputChange}
                    required
                    placeholder="Selecciona un área"
                    options={[
                      { value: 'matematicas', label: 'Matemáticas' },
                      { value: 'lectura-critica', label: 'Lectura Crítica' },
                      { value: 'sociales', label: 'Sociales y Ciudadanas' },
                      { value: 'ciencias', label: 'Ciencias Naturales' },
                      { value: 'ingles', label: 'Inglés' }
                    ]}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <FormSelect
                    label="Dificultad"
                    name="difficulty"
                    value={formData.difficulty}
                    onChange={handleInputChange}
                    required
                    placeholder="Selecciona dificultad"
                    options={[
                      { value: 'easy', label: 'Fácil' },
                      { value: 'medium', label: 'Medio' },
                      { value: 'hard', label: 'Difícil' }
                    ]}
                  />
                </Box>
              </Box>

              {/* Tema y Competencia */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <FormControl fullWidth key={`topic-${formData.area}`}>
                    <InputLabel shrink sx={{ bgcolor: 'background.paper', px: 1 }}>
                      Tema
                    </InputLabel>
                    <Select
                      name="topic"
                      value={
                        formData.topic && getTopicsForArea().some(t => t.id === formData.topic)
                          ? formData.topic 
                          : ''
                      }
                      onChange={handleInputChange}
                      disabled={!formData.area}
                      displayEmpty
                      sx={{ 
                        '& .MuiSelect-select': { 
                          display: 'flex',
                          alignItems: 'center'
                        }
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Ninguno (opcional)</em>
                      </MenuItem>
                      {getTopicsForArea().map((topic) => (
                        <MenuItem key={topic.id} value={topic.id}>
                          {topic.name}
                          {topic.componentName && ` (${topic.componentName})`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <FormControl fullWidth key={`competency-${formData.area}`}>
                    <InputLabel shrink sx={{ bgcolor: 'background.paper', px: 1 }}>
                      Competencia
                    </InputLabel>
                    <Select
                      name="competency"
                      value={
                        formData.competency && getCompetencies().some(c => c.id === formData.competency)
                          ? formData.competency 
                          : ''
                      }
                      onChange={handleInputChange}
                      disabled={!formData.area}
                      displayEmpty
                      sx={{ 
                        '& .MuiSelect-select': { 
                          display: 'flex',
                          alignItems: 'center'
                        }
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Ninguna (opcional)</em>
                      </MenuItem>
                      {getCompetencies().map((comp) => (
                        <MenuItem key={comp.id} value={comp.id}>
                          {comp.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Componente */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <FormControl fullWidth disabled={formData.area !== 'ciencias'} key={`component-${formData.area}`}>
                    <InputLabel shrink sx={{ bgcolor: 'background.paper', px: 1 }}>
                      Componente
                    </InputLabel>
                    <Select
                      name="component"
                      value={
                        formData.component && getComponents().some(c => c.id === formData.component)
                          ? formData.component 
                          : ''
                      }
                      onChange={handleInputChange}
                      displayEmpty
                      sx={{ 
                        '& .MuiSelect-select': { 
                          display: 'flex',
                          alignItems: 'center'
                        }
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>{formData.area === 'ciencias' ? 'Ninguno (opcional)' : 'Solo para Ciencias'}</em>
                      </MenuItem>
                      {formData.area === 'ciencias' && getComponents().map((comp) => (
                        <MenuItem key={comp.id} value={comp.id}>
                          {comp.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }} /> {/* Espacio vacío para balance */}
              </Box>
            </Box>
          </Box>

          {/* Sección de Contexto Compartido (Opcional) */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
              Contexto Compartido (Opcional)
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>¿Qué es el Contexto Compartido?</strong>
              <br />
              Si varias preguntas comparten el mismo texto o imagen inicial, usa el mismo "ID del Set" para todas.
              Solo escribe el contexto en la primera pregunta y define el orden de cada una.
              <br /><br />
              <strong>Ejemplo:</strong> Un texto largo sobre historia con 3 preguntas → ID del Set: "historia-001", Orden: 1, 2, 3
            </Alert>
            
            {/* Selector de Set Existente */}
            {availableSets.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth>
                  <InputLabel shrink sx={{ bgcolor: 'background.paper', px: 1 }}>
                    O selecciona un Set existente
                  </InputLabel>
                  <Select
                    name="question_set_id_selector"
                    value={formData.question_set_id}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        question_set_id: e.target.value,
                      }));
                    }}
                    displayEmpty
                    sx={{ 
                      '& .MuiSelect-select': { 
                        py: 1.5,
                        display: 'flex',
                        alignItems: 'center'
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>Crear nuevo set o no usar set</em>
                    </MenuItem>
                    {availableSets.map((set) => (
                      <MenuItem key={set.set_id} value={set.set_id}>
                        {set.set_id} ({set.area})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 2, minWidth: 0 }}>
                <TextField
                  fullWidth
                  label="ID del Set (opcional)"
                  name="question_set_id"
                  value={formData.question_set_id}
                  onChange={handleInputChange}
                  placeholder="Ej: matematicas-set-001, lectura-texto-02"
                  InputLabelProps={{ shrink: true }}
                  helperText="Usa el mismo ID para preguntas relacionadas o selecciona uno arriba"
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Orden en el Set (opcional)"
                  name="question_order"
                  value={formData.question_order}
                  onChange={handleInputChange}
                  placeholder="1, 2, 3..."
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 1 }}
                  helperText="Posición en el grupo"
                />
              </Box>
            </Box>
          </Box>

          {/* Sección de Contexto */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
              Texto e Imagen de Contexto
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Texto de Contexto (opcional)"
                name="context_text"
                value={formData.context_text}
                onChange={handleInputChange}
                placeholder="Texto compartido para múltiples preguntas"
                InputLabelProps={{ shrink: true }}
              />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Imagen de Contexto (opcional)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  sx={{ minWidth: 200 }}
                >
                  Subir Imagen de Contexto
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, 'context')}
                  />
                </Button>
                {contextImagePreview && (
                  <Card sx={{ mt: 2 }}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={contextImagePreview}
                      alt="Context preview"
                    />
                    <CardActions>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => removeImage('context')}
                      >
                        Eliminar
                      </Button>
                    </CardActions>
                  </Card>
                )}
              </Box>
            </Box>
          </Box>

          {/* Sección de Pregunta */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
              Pregunta
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Imagen de Pregunta (opcional)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  sx={{ minWidth: 200 }}
                >
                  Subir Imagen de Pregunta
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, 'question')}
                  />
                </Button>
                {questionImagePreview && (
                  <Card sx={{ mt: 2 }}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={questionImagePreview}
                      alt="Question preview"
                    />
                    <CardActions>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => removeImage('question')}
                      >
                        Eliminar
                      </Button>
                    </CardActions>
                  </Card>
                )}
              </Box>

              <TextField
                fullWidth
                required
                multiline
                rows={6}
                label="Pregunta"
                name="question"
                value={formData.question}
                onChange={handleInputChange}
                placeholder="Escribe la pregunta aquí"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>

          {/* Sección de Opciones */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
              Opciones de Respuesta
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {formData.options.map((option, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Chip
                    label={String.fromCharCode(65 + index)}
                    color={
                      formData.correct_answer === String.fromCharCode(65 + index)
                        ? 'success'
                        : 'default'
                    }
                    sx={{ minWidth: 40, height: 40 }}
                  />
                  <TextField
                    fullWidth
                    required
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    placeholder={`Opción ${String.fromCharCode(65 + index)}`}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              ))}

              <Box sx={{ mt: 1 }}>
                <FormSelect
                  label="Respuesta Correcta"
                  name="correct_answer"
                  value={['A', 'B', 'C', 'D'].includes(formData.correct_answer) ? formData.correct_answer : ''}
                  onChange={handleInputChange}
                  required
                  placeholder="Selecciona la respuesta correcta"
                  options={[
                    { value: 'A', label: 'A' },
                    { value: 'B', label: 'B' },
                    { value: 'C', label: 'C' },
                    { value: 'D', label: 'D' }
                  ]}
                />
              </Box>
            </Box>
          </Box>

          {/* Sección de Explicación */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
              Explicación
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Explicación de la respuesta correcta"
              name="explanation"
              value={formData.explanation}
              onChange={handleInputChange}
              placeholder="Explica por qué esta es la respuesta correcta"
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          {/* Sección de Estado */}
          <Box sx={{ mb: 4 }}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 3, 
                bgcolor: formData.status === 'active' ? 'success.lighter' : 'grey.100',
                borderColor: formData.status === 'active' ? 'success.main' : 'grey.400',
              }}
            >
              <FormSelect
                label="Estado"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                placeholder="Selecciona el estado"
                options={[
                  { value: 'active', label: '✓ Activa' },
                  { value: 'inactive', label: '○ Inactiva' }
                ]}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {formData.status === 'active' 
                  ? 'Esta pregunta está activa y puede aparecer en exámenes' 
                  : 'Esta pregunta está inactiva y no aparecerá en exámenes'}
              </Typography>
            </Paper>
          </Box>

          {/* Botones */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/teacher/questions')}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading
                ? 'Guardando...'
                : isEditing
                ? 'Actualizar Pregunta'
                : 'Crear Pregunta'}
            </Button>
          </Box>
        </form>
      </Paper>
      )}
    </Container>
  );
};

export default QuestionForm;
